import {ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {MessageService, PrimeTemplate} from 'primeng/api';
import {TableModule} from 'primeng/table';
import {Tag} from 'primeng/tag';
import {InputText} from 'primeng/inputtext';
import {ButtonModule} from 'primeng/button';
import {Select} from 'primeng/select';
import {ProgressSpinner} from 'primeng/progressspinner';
import {finalize, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {TypedTableRowDirective} from '../../shared/typed-table-row.directive';
import {UserLinkComponent} from '../../shared/components/user-link/user-link.component';
import {lastSyncStatusToTag, loginStatusToTag, nycProfileStatusToTag, Severity} from '../../shared/enum-to-tag';

import {UserSyncClient, UsersClient} from '../../api-client/api/api';
import {CircuitBreakerStatusDto} from '../../api-client/model/circuit-breaker-status-dto';
import {UserRegistrationDto} from '../../api-client/model/user-registration-dto';
import {UserSyncQueueStateDto} from '../../api-client/model/user-sync-queue-state-dto';
import {UserSyncQueueType} from '../../api-client/model/user-sync-queue-type';

const QUEUE_LABELS: Record<UserSyncQueueType, string> = {
  scheduled: 'Scheduled queue',
  priority: 'Priority queue',
};

type FilterValue = 'all' | 'to-register' | 'sync-failed';

type FilterOption = { label: string; value: FilterValue };

const filterOptions: FilterOption[] = [
  {label: 'All', value: 'all'},
  {label: 'To register', value: 'to-register'},
  {label: 'Sync failed', value: 'sync-failed'},
];

type AutoCreateStatusTag = { value: string; severity: Severity };

@Component({
  selector: 'homey-nyc-sync-page',
  standalone: true,
  imports: [
    FormsModule,
    DatePipe,
    PrimeTemplate,
    TableModule,
    Tag,
    TypedTableRowDirective,
    UserLinkComponent,
    InputText,
    ButtonModule,
    Select,
    ProgressSpinner,
  ],
  templateUrl: './nyc-sync-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NycSyncPageComponent implements OnInit {
  private readonly userSyncClient = inject(UserSyncClient);
  private readonly usersClient = inject(UsersClient);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private readonly users = signal<UserRegistrationDto[]>([]);
  protected readonly totalCount = signal(0);
  protected readonly search = signal('');
  protected readonly filter = input<FilterValue>('to-register');
  protected readonly selectedFilter = computed(() => this.filter() ?? 'to-register');
  protected readonly filterOptions = filterOptions;

  protected readonly nycProfileStatusToTag = nycProfileStatusToTag;
  protected readonly lastSyncStatusToTag = lastSyncStatusToTag;
  protected readonly loginStatusToTag = loginStatusToTag;

  protected readonly queueStates = signal<UserSyncQueueStateDto[]>([]);
  protected readonly queueLoading = signal(true);
  protected readonly togglingQueue = signal<UserSyncQueueType | undefined>(undefined);
  protected readonly queueLabels = QUEUE_LABELS;

  protected readonly circuitBreakerStatus = signal<CircuitBreakerStatusDto | undefined>(undefined);
  protected readonly circuitBreakerLoading = signal(true);

  protected readonly sortedUsers = computed(() => this.users());

  private readonly searchTrigger$ = new Subject<{ search: string; view: FilterValue }>();

  constructor() {
    this.searchTrigger$
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => a.search === b.search && a.view === b.view),
        switchMap(params =>
          this.usersClient.searchRegistrations(
            params.search || undefined,
            params.view === 'all' ? undefined : params.view
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(result => {
        this.users.set(result.users);
        this.totalCount.set(result.totalCount);
      });

    this.reloadQueues();
    this.loadCircuitBreakerStatus();
  }

  ngOnInit() {
    this.triggerSearch(this.search(), this.selectedFilter());
  }

  private triggerSearch(search: string, view: FilterValue) {
    this.searchTrigger$.next({search, view});
  }

  protected onSearchChange(value: string) {
    this.search.set(value);
    this.triggerSearch(value, this.selectedFilter());
  }

  protected onFilterChange(value: FilterValue) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {filter: value},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.triggerSearch(this.search(), value);
  }

  protected labelFor(queue: UserSyncQueueType): string {
    return QUEUE_LABELS[queue];
  }

  protected reloadQueues() {
    this.queueLoading.set(true);
    this.userSyncClient
      .getUserSyncQueues()
      .pipe(finalize(() => this.queueLoading.set(false)))
      .subscribe({
        next: queues => {
          this.queueStates.set([queues.scheduled, queues.priority]);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Unable to refresh user sync queues',
          });
        },
      });
  }

  protected toggleListener(state: UserSyncQueueStateDto) {
    const target = state.queue;
    const enable = !state.listenerEnabled;
    this.togglingQueue.set(target);
    this.userSyncClient
      .setUserSyncQueueListener(target, {enable})
      .pipe(finalize(() => this.togglingQueue.set(undefined)))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: `Listener ${enable ? 'resumed' : 'paused'}`,
            detail: QUEUE_LABELS[target],
          });
          this.reloadQueues();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: `Failed to ${enable ? 'resume' : 'pause'} listener`,
            detail: QUEUE_LABELS[target],
          });
        },
      });
  }

  protected isToggling(queue: UserSyncQueueType): boolean {
    return this.togglingQueue() === queue;
  }

  protected loadCircuitBreakerStatus() {
    this.circuitBreakerLoading.set(true);
    this.userSyncClient
      .getCircuitBreakerStatus()
      .pipe(finalize(() => this.circuitBreakerLoading.set(false)))
      .subscribe({
        next: status => this.circuitBreakerStatus.set(status),
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Unable to load circuit breaker status',
          });
        },
      });
  }

  protected autoCreateStatusTag(user: UserRegistrationDto): AutoCreateStatusTag {
    switch (user.autoCreateStatus) {
      case 'in-progress':
        return {value: 'In Progress', severity: 'warn'};
      case 'failed':
        return {value: 'Failed', severity: 'danger'};
      case 'pending':
        return {value: 'Pending', severity: 'secondary'};
    }
  }
}
